import {
  brandOptions,
  organizations,
  suppliers,
  contactPersons,
  adminUnits,
} from './itemsFixtureData';

/**
 * Deterministic probes for the D2 Playwright "Placeholder text & Mixed-values semantics" epic
 * (dl-64265, Testmo folder 76739 part 2/3): the Items bulk-edit modal's placeholder text — what
 * an untouched field's input prompts, what happens once it's changed and cleared again — and how
 * a field whose selected items disagree is presented. Isolable via `?search=PlaceholderProbe`
 * (unique substring, absent from every generated/other fixture name), with a sub-word per scenario
 * for further narrowing (`?search=PlaceholderProbe Unchanged`, `?search=PlaceholderProbe Mixed`).
 *
 * Base shape mirrors `bulkUpdateProbeBase` / `bulkEditClearFieldBase`
 * (`bulkUpdateProbeFixtureData.ts` / `bulkEditClearFieldFixtureData.ts`) — split into its own file
 * for the same reason those were: self-contained, reviewable, and keeps `items.ts` under the
 * repo's 800-line lint cap.
 *
 * Field-type coverage (verified against the real bulk-edit modal source, not assumed from Testmo
 * prose — see `BulkEditModal.tsx`, `columnTypes/modal/{text,dropdown,status,number,date,
 * multiselect}.tsx`, `items/utils/fieldAccess.ts`, `items/components/config-table.ts`):
 * - Native dropdown: `brand` (Brand), `adminUnit` (Unit, optional)
 * - Native status-dropdown: `isActive` (Status, required, `type: 'status'`)
 * - Native number: `defaultQty` (Quantity, required)
 * - Text CF: customFieldId 9 ("Internal notes", optional) — native `description` is
 *   `excludeFromBulkEdit: true`, so this is the only free-text field the modal exposes.
 * - Date CF: customFieldId 11 ("Launch date", optional)
 * - Number CF: customFieldId 13 ("Target weight", optional)
 * - Single-select dropdown CF: customFieldId 10 ("Care instruction", optional) — a SECOND
 *   dropdown besides Brand/Unit/Status, needed so the mixed-values fixtures can diverge on a
 *   custom field as well as a native one (TC-007/TC-035/TC-038 all ask for "a custom field too").
 *
 * Two fixture groups:
 * - `phUnchangedA`/`B` — identical on every field above. Used by the "placeholder on an untouched
 *   field" and "placeholder through the change lifecycle" stories (dl-64305/64307/64308/64309/
 *   64311/64312/64318).
 * - `phMixedA`/`B`/`C` — three items, each with a DIFFERENT Brand (three-way divergence, not just
 *   two, per TC-007's "3+ items" adversarial ask) and a DIFFERENT Care instruction on B only (A
 *   and C agree — "two agree, one differs" on a second field). Target weight is set on A/C but
 *   absent on B (empty-vs-set divergence, the case neither Testmo batch covers per TC-038's own
 *   adversarial note). Internal notes and Launch date are identical across all three (agreeing
 *   fields, the "positive control" contrast the mixed-values stories ask for). Used by
 *   dl-64313/64314/64315/64316/64317.
 */
const placeholderProbeBase = {
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  logo: null,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  description: null,
};

/** Single-select allowed values for the "Care instruction" CF (customFieldId 10) — mirrors the
 *  (unexported) list in `itemsFixtureData.ts`; duplicated here rather than exported from there to
 *  avoid widening that file's public surface for a value only this fixture group needs. */
const careInstructionValues = [
  { id: 101, label: 'Machine wash', userDefinedId: null, parentId: null },
  { id: 102, label: 'Hand wash', userDefinedId: null, parentId: null },
  { id: 103, label: 'Dry clean only', userDefinedId: null, parentId: null },
];

/** Text-type CF value entry (customFieldId 9, "Internal notes"). */
const internalNoteCf = (id: number, value: string) => ({
  id,
  customFieldId: 9,
  name: 'Internal notes',
  userDefinedId: 'NOTES',
  type: 2,
  value,
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: false,
  internalOnly: true,
  maxChar: 500,
  isMultiLine: true,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: [] as unknown[],
});

/** Date-type CF value entry (customFieldId 11, "Launch date"). */
const launchDateCf = (id: number, value: string) => ({
  id,
  customFieldId: 11,
  name: 'Launch date',
  userDefinedId: 'LAUNCH',
  type: 1,
  value,
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: [] as unknown[],
});

/** Numeric-type CF value entry (customFieldId 13, "Target weight"). */
const targetWeightCf = (id: number, value: number) => ({
  id,
  customFieldId: 13,
  name: 'Target weight',
  userDefinedId: 'WEIGHT',
  type: 4,
  value: String(value),
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 2,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: [] as unknown[],
});

/** Single-select AllowedValue CF entry (customFieldId 10, "Care instruction"). */
const careInstructionCf = (id: number, allowedValue: (typeof careInstructionValues)[number]) => ({
  id,
  customFieldId: 10,
  name: 'Care instruction',
  userDefinedId: 'CARE',
  type: 0,
  value: allowedValue.label,
  itemCustomFieldAllowedValueId: allowedValue.id,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: careInstructionValues,
});

// ---------------------------------------------------------------------------
// Unchanged pair — every field above shares the same value.
// ---------------------------------------------------------------------------
export const phUnchangedA = {
  ...placeholderProbeBase,
  id: 90400,
  name: 'PlaceholderProbe Unchanged A',
  userDefinedId: 'IT-90400',
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 5,
  customFields: [
    internalNoteCf(90400009, 'Shared internal note'),
    launchDateCf(90400011, '2026-04-01'),
    targetWeightCf(90400013, 10),
    careInstructionCf(90400010, careInstructionValues[0]),
  ],
};
export const phUnchangedB = {
  ...phUnchangedA,
  id: 90401,
  name: 'PlaceholderProbe Unchanged B',
  userDefinedId: 'IT-90401',
  customFields: [
    internalNoteCf(90401009, 'Shared internal note'),
    launchDateCf(90401011, '2026-04-01'),
    targetWeightCf(90401013, 10),
    careInstructionCf(90401010, careInstructionValues[0]),
  ],
};

// ---------------------------------------------------------------------------
// Mixed triple — Brand differs across all three; Care instruction differs on B only (A/C agree);
// Target weight set on A/C, absent on B (empty-vs-set divergence); Internal notes and Launch date
// agree across all three (the "agreeing field" control the mixed-values stories need alongside a
// divergent one).
// ---------------------------------------------------------------------------
export const phMixedA = {
  ...placeholderProbeBase,
  id: 90410,
  name: 'PlaceholderProbe Mixed A',
  userDefinedId: 'IT-90410',
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 5,
  customFields: [
    internalNoteCf(90410009, 'Agreeing note'),
    launchDateCf(90410011, '2026-05-15'),
    targetWeightCf(90410013, 20),
    careInstructionCf(90410010, careInstructionValues[0]),
  ],
};
export const phMixedB = {
  ...placeholderProbeBase,
  id: 90411,
  name: 'PlaceholderProbe Mixed B',
  userDefinedId: 'IT-90411',
  brand: brandOptions[1 % brandOptions.length],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 5,
  customFields: [
    internalNoteCf(90411009, 'Agreeing note'),
    launchDateCf(90411011, '2026-05-15'),
    // No Target weight entry — empty-vs-set divergence against A/C's 20.
    careInstructionCf(90411010, careInstructionValues[1]),
  ],
};
export const phMixedC = {
  ...placeholderProbeBase,
  id: 90412,
  name: 'PlaceholderProbe Mixed C',
  userDefinedId: 'IT-90412',
  brand: brandOptions[2 % brandOptions.length],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 5,
  customFields: [
    internalNoteCf(90412009, 'Agreeing note'),
    launchDateCf(90412011, '2026-05-15'),
    targetWeightCf(90412013, 20),
    careInstructionCf(90412010, careInstructionValues[0]),
  ],
};

export const placeholderMixedValuesItems = [
  phUnchangedA,
  phUnchangedB,
  phMixedA,
  phMixedB,
  phMixedC,
];
